/**
 * NotificationBell — top-nav notification dropdown.
 * Polls /api/notifications/feed every 60s.
 */
import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Bell, ShieldAlert, Truck, FileCheck2, Package, CheckCheck, Settings } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICONS = {
  fraud: ShieldAlert,
  tracking: Truck,
  kys_review: FileCheck2,
  shipstation: Package,
};

const SEV_COLOR = {
  high: 'text-rose-500',
  warning: 'text-amber-500',
  info: 'text-slate-500',
};

function timeAgo(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [feed, setFeed] = useState({ items: [], unread_count: 0 });
  const ref = useRef(null);
  const token = localStorage.getItem('token');

  const fetchFeed = async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API_URL}/api/notifications/feed`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setFeed(await r.json());
    } catch {}
  };

  useEffect(() => {
    fetchFeed();
    const id = setInterval(fetchFeed, 60_000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (!ref.current || !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const markAllRead = async () => {
    await fetch(`${API_URL}/api/notifications/mark-read`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    setFeed((f) => ({ ...f, unread_count: 0 }));
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        data-testid="notif-bell"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-slate-600 dark:text-slate-300" />
        {feed.unread_count > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notif-badge"
          >
            {feed.unread_count > 9 ? '9+' : feed.unread_count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 md:w-96 rounded-xl bg-white dark:bg-slate-900 shadow-xl border border-slate-200 dark:border-slate-800 z-50 overflow-hidden"
          data-testid="notif-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800">
            <div>
              <h3 className="font-semibold text-sm">Notifications</h3>
              <p className="text-xs text-slate-500">
                {feed.unread_count > 0 ? `${feed.unread_count} unread` : 'You’re all caught up'}
              </p>
            </div>
            {feed.unread_count > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                data-testid="notif-mark-read"
              >
                <CheckCheck className="w-3 h-3" />
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {(feed.items || []).length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400" data-testid="notif-empty">
                No recent activity
              </div>
            ) : (
              feed.items.map((n) => {
                const Icon = ICONS[n.kind] || Bell;
                return (
                  <Link
                    key={n.id}
                    to={n.link || '/dashboard'}
                    onClick={() => setOpen(false)}
                    className="flex gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/60 border-b border-slate-100 dark:border-slate-800 last:border-b-0"
                    data-testid={`notif-item-${n.kind}`}
                  >
                    <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${SEV_COLOR[n.severity] || SEV_COLOR.info}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">
                        {n.title}
                      </p>
                      {n.body && (
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-2 mt-0.5">
                          {n.body}
                        </p>
                      )}
                      <p className="text-[10px] text-slate-400 mt-0.5">{timeAgo(n.created_at)}</p>
                    </div>
                  </Link>
                );
              })
            )}
          </div>

          <Link
            to="/dashboard/settings/notifications"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-4 py-2.5 text-xs text-slate-500 hover:text-indigo-600 hover:bg-slate-50 dark:hover:bg-slate-800/60 border-t border-slate-100 dark:border-slate-800"
            data-testid="notif-settings-link"
          >
            <Settings className="w-3.5 h-3.5" />
            Notification settings · email &amp; SMS
          </Link>
        </div>
      )}
    </div>
  );
}
